import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { IS_UAE_ENV } from '@modules/core/utils';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import useGenerateDigitalDocument from '@modules/opportunities/entities/bankApplication/mutation/useGenerateDigitalDocument';
import useSendDigitalDocument from '@modules/opportunities/entities/bankApplication/mutation/useSendDigitalDocument';
import {
  addToAmplitudeEventContext,
  clearAmplitudeEventContext,
  trackAmplitudeEvent,
} from '@shared/analytics/amplitude';
import {
  BANK_APPLICATION_STATUS,
  DIGITAL_DOCUMENT,
} from '@modules/opportunities/entities/bankApplication/const';
import {
  ArrowUpRight, Check, FileScan, Upload
} from '@huspy/briks-icons';
import { BankSubmissionStatusBadge } from '@modules/opportunities/presentation/pages/CaseCreation/components/BankSubmissionStatusBadge';
import { useDisclosure } from '@mantine/hooks';
import BankIcon from '@components/v2/BankIcon';
import { Divider } from '@huspy/styled-system/jsx';
import briksTheme from '@shared/briks-theme';
import {
  DIGITAL_BANK_APPLICATION_DOCUMENT_STATUS, DigitalBankApplicationDocumentStatusType, DigitalBankApplicationLinks
} from '@modules/opportunities/entities/bankApplication/api/dto/digitalBankApplications.dto';
import { OpportunityApplicant } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { APPLICANT_TYPE } from '@modules/opportunities/entities/opportunity/const';
import { useVaultReview } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/VaultReviewSlido/useVaultReview';
import { VaultReviewSlido } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/VaultReviewSlido';
import { UploadSlido } from '../UploadSlido';
import { EsignatureSlido, ShareLinks } from '../EsignatureSlido';

const baseTranslationPath = 'opportunity.caseSubmission.bankForms.bankApplication';

type BankSectionType = {
  bankApplication: BankApplication;
  opportunityId: string;
  clientId: string | null;
  hasDigitalBankApplication: boolean;
  hasErrors: boolean;
  digitalBankApplicationId: string | null;
  digitalBankApplicationLinks: DigitalBankApplicationLinks | null;
  digitalBankApplicationStatus: DigitalBankApplicationDocumentStatusType | null;
  applicants: OpportunityApplicant<'AE'>[];
  isCommercial: boolean;
};

type DigitalDocumentButtonProps = {
  opportunityExternalId: string;
  bankApplicationExternalId: string;
  digitalBankApplicationId: string | null;
  digitalBankApplicationLink: string | undefined | null;
  digitalBankApplicationStatus: DigitalBankApplicationDocumentStatusType | null;
  shareLinks: ShareLinks;
  hasDigitalBankApplication: boolean;
  clientExternalId?: string | null;
  bankExternalId?: string;
  isCommercial: boolean;
};

const DigitalDocumentButton = ({
  opportunityExternalId,
  bankApplicationExternalId,
  digitalBankApplicationLink,
  hasDigitalBankApplication,
  digitalBankApplicationId,
  clientExternalId,
  bankExternalId,
  digitalBankApplicationStatus,
  shareLinks,
  isCommercial,
}: DigitalDocumentButtonProps) => {
  const {
    mutateAsync: generateDigitalDocument,
    isPending: generatePending,
  } = useGenerateDigitalDocument(opportunityExternalId);
  const {
    mutateAsync: sendDigitalDoc,
    isPending: sendPending,
  } = useSendDigitalDocument(opportunityExternalId);

  const {
    isOpened: isVaultReviewOpened,
    handleVaultReview,
    close: closeVaultReviewSlido,
    isSubmitVaultLoading,
    vaultError,
  } = useVaultReview(
    opportunityExternalId,
    clientExternalId!
  );
  const { t } = useTranslation();

  const handleClick = async () => {
    clearAmplitudeEventContext();
    addToAmplitudeEventContext({
      client_id: clientExternalId,
      case_id: opportunityExternalId,
      bank: bankExternalId,
    });

    const shouldOpenVaultReview = await handleVaultReview();
    if (shouldOpenVaultReview) {
      return;
    }
    if (digitalBankApplicationLink) {
      trackAmplitudeEvent(DIGITAL_DOCUMENT.viewDigitalDocument);
      window.open(digitalBankApplicationLink, '_blank');
    } else if (digitalBankApplicationId) {
      await sendDigitalDoc({
        bankApplicationExternalId,
        digitalDocumentId: digitalBankApplicationId,
      });
    } else {
      const generateResponse = await generateDigitalDocument({ bankApplicationExternalId });
      if (generateResponse.digital_bank_application_external_id) {
        await sendDigitalDoc({
          bankApplicationExternalId,
          digitalDocumentId:
            generateResponse.digital_bank_application_external_id,
        });
      }
    }
  };

  const isLoading = generatePending || sendPending || isSubmitVaultLoading;

  if (!IS_UAE_ENV || isCommercial) return null;

  if (!hasDigitalBankApplication) {
    return (
      <Button
        variant='primary'
        type='button'
        disabled
        data-testid='digital-bank-app-btn'
      >
        <FileScan width='20px' height='20px' color='white' />
        <Text>
          {t(`${baseTranslationPath}.unavailableDigitalBankApplication`)}
        </Text>
      </Button>
    );
  }
  if (digitalBankApplicationLink) {
    const isCompleted = digitalBankApplicationStatus === DIGITAL_BANK_APPLICATION_DOCUMENT_STATUS.COMPLETED;
    const buttonText = `${baseTranslationPath}.${isCompleted ? 'viewSignedBankForm' : 'viewFilledForm'}`;
    return (
      <Flex gap='2' flexDirection={ isCompleted ? 'row-reverse' : 'row' }>
        <Button
          onClick={ async () => {
            await handleClick();
          } }
          variant={ isCompleted ? 'primary' : 'outline' }
          loading={ isLoading }
          type='button'
          data-testid='digital-bank-app-btn'
        >
          <Text>{t(buttonText)}</Text>
          <ArrowUpRight color={ isCompleted ? 'white' : 'black' } />
        </Button>
        <EsignatureSlido
          opportunityExternalId={ opportunityExternalId }
          bankApplicationExternalId={ bankApplicationExternalId }
          digitalBankApplicationId={ digitalBankApplicationId! }
          isEdit={ isCompleted }
          shareLinks={ shareLinks }
        />
      </Flex>
    );
  }

  return (
    <Button
      onClick={ async () => {
        await handleClick();
      } }
      variant='primary'
      loading={ isLoading }
      type='button'
      disabled={ isLoading }
      data-testid='digital-bank-app-btn'
    >
      <FileScan width='20px' height='20px' color='white' />
      <Text>
        {t(`${baseTranslationPath}.generateDigitalBankApplicationForm`)}
      </Text>
      {
        isVaultReviewOpened
        && (
          <VaultReviewSlido
            opened={ isVaultReviewOpened }
            close={ closeVaultReviewSlido }
            opportunityId={ opportunityExternalId }
            vaultError={ vaultError }
          />
        )
      }
    </Button>
  );
};

const getBorderColor = (
  hasFormsUploaded: boolean,
  hasErrors: boolean,
  isSubmitted: boolean
) => {
  if (isSubmitted) return briksTheme.colors['neutral.200'];
  if (hasFormsUploaded) return briksTheme.colors['success.500'];
  if (hasErrors) return briksTheme.colors['error.500'];
  return briksTheme.colors['neutral.200'];
};

export const BankSection = ({
  bankApplication,
  opportunityId,
  clientId,
  hasDigitalBankApplication,
  hasErrors,
  applicants,
  digitalBankApplicationId,
  digitalBankApplicationLinks,
  digitalBankApplicationStatus,
  isCommercial,
}: BankSectionType) => {
  const { t } = useTranslation();
  const [isOpen, { open, close }] = useDisclosure(false);
  const [isUploading, setIsUploading] = useState(false);
  const { bank_details, documents } = bankApplication;
  const hasFormsUploaded = documents && documents.length > 0;
  const isSubmitted = bankApplication.status !== BANK_APPLICATION_STATUS.draft;

  const getShareLinks = () => {
    if (!applicants || !digitalBankApplicationLinks) {
      return [];
    }
    return applicants.map((applicant) => {
      const signerField = applicant.applicant_type === APPLICANT_TYPE.mainApplicant
        ? 'main_signer_link'
        : 'co_signer_link';
      const fullName = `${applicant.first_name} ${applicant.last_name}`;
      const link = digitalBankApplicationLinks[signerField];
      return ({
        fullName,
        applicantType: applicant.applicant_type,
        link,
      });
    });
  };

  const renderBankName = () => (
    <Flex align='center' gap='3'>
      <BankIcon icon={ bank_details.icon } w='42px' h='42px' />
      <Text>{bank_details.name}</Text>
    </Flex>
  );
  const renderButtons = () => {
    if (isSubmitted) {
      return (
        <BankSubmissionStatusBadge
          bankSubmissionChannelType={ bankApplication.bank_submission_channel_type }
        />
      );
    }
    return (
      <Flex gap='2' justify='end'>
        <DigitalDocumentButton
          opportunityExternalId={ opportunityId }
          clientExternalId={ clientId }
          bankExternalId={ bank_details.external_id }
          bankApplicationExternalId={ bankApplication.bank_application_external_id }
          hasDigitalBankApplication={ hasDigitalBankApplication }
          digitalBankApplicationId={ digitalBankApplicationId }
          digitalBankApplicationLink={ digitalBankApplicationLinks?.main_link }
          digitalBankApplicationStatus={ digitalBankApplicationStatus }
          shareLinks={ getShareLinks() }
          isCommercial={ isCommercial }
        />
        <Button
          type='button'
          variant='ghost'
          shadow='shadow-4'
          borderRadius='full'
          onClick={ open }
          disabled={ isUploading }
          loading={ isUploading }
          data-testid='upload-bank-form-btn'
          p='3'
          w='fit'
        >
          <Upload width='20px' height='20px' />
        </Button>
      </Flex>
    );
  };
  const renderDetails = () => {
    if (!hasFormsUploaded || isSubmitted) {
      return null;
    }
    const isCompleted = digitalBankApplicationStatus === DIGITAL_BANK_APPLICATION_DOCUMENT_STATUS.COMPLETED;
    return (
      <>
        <Divider />
        <Flex px='6' py='2' justify='space-between'>
          <Flex
            align='center'
            visibility={ isCompleted ? 'visible' : 'hidden' }
            gap='1'
          >
            <Check color={ briksTheme.colors['success.500'] } />
            <Text
              size='lg'
              fontWeight='regular'
            >
              {t(`${baseTranslationPath}.uploadedSignedForm`)}
            </Text>
          </Flex>
          <Text
            size='lg'
            color='text-tertiary'
            fontWeight='regular'
            onClick={ open }
            cursor='pointer'
          >
            {t(`${baseTranslationPath}.numOfDocuments`, { num: documents.length })}
          </Text>
        </Flex>
      </>
    );
  };

  return (
    <>
      <Flex direction='column' gap={ 2 }>
        <Flex
          borderRadius='radius-4'
          borderWidth='1'
          backgroundColor='white'
          direction='column'
          style={ {
            borderColor: getBorderColor(
              hasFormsUploaded,
              !!hasErrors,
              isSubmitted
            ),
          } }
        >
          <Flex justify='space-between' align='center' padding='6'>
            {renderBankName()}
            {renderButtons()}
          </Flex>
          {renderDetails()}
        </Flex>
        {hasErrors ? (
          <Text color='text-error-primary_alt' size='md'>
            {t(`${baseTranslationPath}.noFilesUploaded`)}
          </Text>
        ) : null}
      </Flex>
      <UploadSlido
        documents={ documents }
        close={ close }
        isOpen={ isOpen }
        hasFormsUploaded={ hasFormsUploaded }
        bankApplicationId={ bankApplication.bank_application_external_id }
        opportunityId={ opportunityId }
        bankId={ bankApplication.bank_details.external_id }
        clientId={ clientId }
        setIsUploading={ (_isUploading: boolean) => setIsUploading(_isUploading) }
      />
    </>
  );
};
